import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  InputNumber,
  Select,
  message, 
  Typography, 
  Space, 
  Popconfirm,
  Tag,
  Row,
  Col,
  Upload,
  Divider,
  Grid,
  Switch,
  theme,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  ShoppingOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { productsService } from '../../services/api';
import SATCatalogSearch from '../../components/Common/SATCatalogSearch';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;
const { useBreakpoint } = Grid;

const SAMPLE_PRODUCTS_CSV = `Nombre,Descripción,Clave Producto SAT,Clave Unidad SAT,Precio Unitario,Tipo
"Consultoría de Software","Servicios de desarrollo y consultoría",81111500,E48,1500.00,servicio
"Licencia Anual","Licencia de uso anual de software",43231500,E48,12000.00,producto
`;

const Products = () => {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [bottomPaginationVisible, setBottomPaginationVisible] = useState(false);
  const topPaginationRef = React.useRef(null);
  const [form] = Form.useForm();
  const screens = useBreakpoint();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setBottomPaginationVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );

    if (topPaginationRef.current) {
      observer.observe(topPaginationRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDownloadSampleCsv = () => {
    const blob = new Blob([SAMPLE_PRODUCTS_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample_products.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    setLoading(true);
    try {
      const result = await productsService.importProductsCsv(formData);
      message.success(`Importación completada: ${result.results.success} exitosos, ${result.results.failed} fallidos`);
      if (result.results.failed > 0) {
           message.warning('Algunos registros no se pudieron importar. Revisa la consola para más detalles.');
           console.log('Errores de importación:', result.results.errors);
      }
      setImportModalVisible(false);
      fetchProducts(searchText);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al importar CSV');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const fetchProducts = async (search = '') => {
    setLoading(true);
    try {
      const response = await productsService.getProducts(search);
      setProducts(response.data || []);
    } catch (error) {
      message.error('Error al cargar productos/servicios');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchProducts(value);
  };

  const showModal = (product = null) => {
    setEditingProduct(product);
    setModalVisible(true);
    if (product) {
      form.setFieldsValue({
        name: product.name,
        tipo: product.type === 1 ? 'servicio' : 'producto',
        descripcion: product.description,
        precio_unitario: product.price,
        clave_producto_servicio: product.claveprod,
        clave_unidad: product.claveunidad,
        apply_taxes: product.apply_taxes !== undefined ? Boolean(product.apply_taxes) : true
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ apply_taxes: true });
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingProduct(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (editingProduct) {
        await productsService.updateProduct(editingProduct.id, values);
        message.success('Producto/servicio actualizado exitosamente');
      } else {
        await productsService.createProduct(values);
        message.success('Producto/servicio creado exitosamente');
      }
      setModalVisible(false);
      setEditingProduct(null);
      form.resetFields();
      fetchProducts(searchText);
    } catch (error) {
      message.error('Error al guardar producto/servicio');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      await productsService.deleteProduct(id);
      message.success('Producto/servicio eliminado exitosamente');
      fetchProducts(searchText);
    } catch (error) {
      message.error('Error al eliminar producto/servicio');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Precio Unitario',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      render: (value) => `$${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      title: 'Clave SAT',
      dataIndex: 'claveprod',
      key: 'claveprod',
      width: 120,
    },
    {
      title: 'Clave SAT Unidad',
      dataIndex: 'claveunidad',
      key: 'claveunidad',
      width: 120,
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (tipo) => (
        <Tag color={tipo === 0 ? 'blue' : 'green'}>
          {tipo === 0 ? 'PRODUCTO' : 'SERVICIO'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            size="small"
          />
          <Popconfirm
            title="¿Estás seguro de eliminar este producto/servicio?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: screens.xs ? '16px' : '24px' }}>
      <Card>
        <div style={{ 
          display: 'flex', 
          flexDirection: screens.md ? 'row' : 'column',
          justifyContent: 'space-between', 
          alignItems: screens.md ? 'center' : 'flex-start',
          gap: '16px',
          marginBottom: '24px' 
        }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <ShoppingOutlined style={{ marginRight: '8px' }} />
              Productos y Servicios
            </Title>
            <Text type="secondary">
              Gestiona tu catálogo de productos y servicios
            </Text>
          </div>
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: screens.xs ? '100%' : 'auto' }}>
            <Button
              icon={<UploadOutlined />}
              onClick={() => setImportModalVisible(true)}
              block={screens.xs}
            >
              Importar CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
              block={screens.xs}
            >
              Nuevo Producto/Servicio
            </Button>
          </Space>
        </div>

        <Divider />

        <Row style={{ marginBottom: '16px' }}>
          <Col xs={24} md={12} lg={8}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Input
                placeholder="Buscar productos/servicios..."
                allowClear
                onChange={(e) => {
                  if (!e.target.value) handleSearch('');
                  setSearchText(e.target.value);
                }}
                onPressEnter={() => handleSearch(searchText)}
                style={{ borderRadius: '8px' }}
              />
              <Button 
                type="primary" 
                icon={<SearchOutlined />} 
                onClick={() => handleSearch(searchText)}
                style={{ borderRadius: '8px' }}
              />
            </div>
          </Col>
        </Row>

        <div ref={topPaginationRef} />
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            position: bottomPaginationVisible ? ['topRight', 'bottomRight'] : ['topRight'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} productos/servicios`,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={editingProduct ? 'Editar Producto/Servicio' : 'Nuevo Producto/Servicio'}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="name"
                label="Nombre"
                rules={[{ required: true, message: 'El nombre es requerido' }]}
              >
                <Input placeholder="Nombre del producto/servicio" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="tipo"
                label="Tipo"
                rules={[{ required: true, message: 'El tipo es requerido' }]}
              >
                <Select placeholder="Selecciona el tipo">
                  <Option value="producto">Producto</Option>
                  <Option value="servicio">Servicio</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="descripcion"
            label="Descripción"
            rules={[{ required: true, message: 'La descripción es requerida' }]}
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Descripción del producto/servicio" 
            />
          </Form.Item>

          <Row gutter={24}>
            <Col span={16}>
              <Form.Item
                name="precio_unitario"
                label="Precio Unitario"
                rules={[{ required: true, message: 'El precio es requerido' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                  addonBefore="$"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="apply_taxes"
                label="¿Agregar Impuestos?"
                valuePropName="checked"
              >
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={24}>
              <Form.Item
                name="clave_producto_servicio"
                label="Clave Producto/Servicio SAT"
                rules={[{ required: true, message: 'La clave SAT es requerida' }]}
              >
                <SATCatalogSearch
                  type="products"
                  placeholder="Buscar en catálogo SAT..."
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="clave_unidad"
                label="Clave Unidad SAT"
                rules={[{ required: true, message: 'La clave de unidad SAT es requerida' }]}
              >
                <SATCatalogSearch
                  type="units"
                  placeholder="Buscar unidad SAT..."
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingProduct ? 'Actualizar' : 'Crear'}
              </Button>
              <Button onClick={handleCancel}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Importar Productos/Servicios desde CSV"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Sube un archivo CSV con las siguientes columnas:</p>
            <Text code>Nombre, Descripcion, Clave Producto SAT, Clave Unidad SAT, Precio Unitario, Tipo</Text>
            <p style={{ marginTop: 10, fontSize: 12, color: token.colorTextTertiary }}>Máximo 50 registros por archivo.</p>
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <Button type="link" onClick={handleDownloadSampleCsv}>
                Descargar machote CSV de ejemplo
              </Button>
            </div>
            <br /><br />
            <Upload.Dragger
                name="file"
                accept=".csv"
                beforeUpload={handleImport}
                showUploadList={false}
            >
                <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                </p>
                <p className="ant-upload-text">Haz clic o arrastra el archivo aquí</p>
            </Upload.Dragger>
        </div>
      </Modal>
    </div>
  );
};

export default Products;
